import React, {ReactElement, useCallback, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useHtmlIframe} from '../../../hooks/useHtmlIFrame';
import {DeviceType} from '../../../incase-domain/common/DeviceType.enum';
import {IElementHtml} from '../../../incase-domain/elements/html/html';
import {isHtmlQuestionReportElement, isHtmlSlottedElement} from '../../../incase-domain/elements/html/htmlSlot';
import {setSidePanelContentAction} from '../../../store/actions/grid';
import {showHtmlEditorModalAction} from '../../../store/actions/html.actions';
import {setHighlightedElementIndex} from '../../../store/actions/slide';
import {SidePanelContent} from '../../../store/actions/types/grid.types';
import {selectedPresentationSelector} from '../../../store/selectors/presentation.selectors';
import {selectedSlideSelector} from '../../../store/selectors/slide.selectors';
import HtmlQuestionReportBridge from './HtmlQuestionReportBridge';
import HtmlSlottedElementPortal from './HtmlSlottedElementPortal';

interface ElementHtmlComponentProps {
    element: IElementHtml;
    device?: DeviceType;
}

function ElementHtmlComponent(props: ElementHtmlComponentProps): ReactElement {
    const {element, device = DeviceType.DESKTOP} = props;
    const dispatch = useDispatch();
    const slide = useSelector(selectedSlideSelector);
    const presentation = useSelector(selectedPresentationSelector);
    const dragStartPosition = useRef<{x: number; y: number} | null>(null);
    const [slotRoots, setSlotRoots] = useState<Record<string, HTMLElement>>({});

    const handleDocumentReady = useCallback((doc: Document) => {
        const next: Record<string, HTMLElement> = {};
        doc.querySelectorAll<HTMLElement>('[data-incase-slot]').forEach((node) => {
            const slotId = node.getAttribute('data-slot-id');
            if (slotId) next[slotId] = node;
        });
        setSlotRoots(next);
    }, []);

    const {containerRef} = useHtmlIframe(element, handleDocumentReady);
    const slottedElements = (slide?.elements ?? []).filter(isHtmlSlottedElement);
    const reportSlides = useMemo(() => {
        const all = presentation?.slides ?? [];
        if (!slide) return all;
        return [slide, ...all.filter((item) => item._id !== slide._id)];
    }, [presentation?.slides, slide]);

    const slotIdAtPoint = (event: React.MouseEvent<HTMLDivElement>): string | null => {
        const iframe = containerRef.current?.querySelector('iframe');
        const doc = iframe?.contentDocument;
        if (!iframe || !doc) return null;

        const frameRect = iframe.getBoundingClientRect();
        const scaleX = iframe.clientWidth ? frameRect.width / iframe.clientWidth : 1;
        const scaleY = iframe.clientHeight ? frameRect.height / iframe.clientHeight : 1;
        const localX = (event.clientX - frameRect.left) / scaleX;
        const localY = (event.clientY - frameRect.top) / scaleY;
        const target = doc.elementFromPoint(localX, localY);
        const slot = target?.closest('[data-incase-slot], .c-questionElement, .c-questionElement__answer_dropdown, .c-multiSelectElement');
        if (!slot) return null;

        return slot.getAttribute('data-slot-id') ?? slot.closest('[data-incase-slot]')?.getAttribute('data-slot-id') ?? null;
    };

    const selectSlottedElement = (slotId: string | null): boolean => {
        if (!slotId || !slide) return false;

        const slotted = slottedElements.find((item) => item.htmlSlotId === slotId);
        if (!slotted) return false;

        const index = slide.elements.findIndex((item) => item.id === slotted.id);
        if (index < 0) return false;

        dispatch(setHighlightedElementIndex(index));
        dispatch(setSidePanelContentAction(SidePanelContent.HIGHLIGHTED_ELEMENT));
        return true;
    };

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
        dragStartPosition.current = {x: event.clientX, y: event.clientY};
        if (selectSlottedElement(slotIdAtPoint(event))) {
            event.stopPropagation();
        }
    };

    const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>): void => {
        if (!dragStartPosition.current || event.button === 2) {
            dragStartPosition.current = null;
            return;
        }

        const {x, y} = dragStartPosition.current;
        const deltaX = Math.abs(event.clientX - x);
        const deltaY = Math.abs(event.clientY - y);

        const dragThreshold = 5;
        if (deltaX < dragThreshold && deltaY < dragThreshold) {
            if (selectSlottedElement(slotIdAtPoint(event))) {
                event.stopPropagation();
            } else {
                event.stopPropagation();
                dispatch(showHtmlEditorModalAction());
            }
        }

        dragStartPosition.current = null;
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
        >
            <div
                style={{
                    background: element.color,
                    color: element.fontColor,
                    width: '100%',
                    height: '100%',
                }}
                className="c-html u-events-none"
                id={`html-id-${element.id}`}
                ref={containerRef}
            />
            {slottedElements.map((slotted) => {
                const root = slotRoots[slotted.htmlSlotId];
                if (!root) return null;

                if (isHtmlQuestionReportElement(slotted)) {
                    return (
                        <HtmlQuestionReportBridge
                            key={slotted.id}
                            element={slotted}
                            slot={root}
                            slides={reportSlides}
                            slideName={slide?.name ?? ''}
                        />
                    );
                }

                return createPortal(<HtmlSlottedElementPortal element={slotted} device={device} />, root, slotted.id);
            })}
        </div>
    );
}

export default ElementHtmlComponent;
